import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, Pause, RotateCcw } from 'lucide-react';

// Import marketing images
import logoImg from '@/assets/marketing-logo.jpg';
import discoveryImg from '@/assets/marketing-discovery.jpg';
import storiesImg from '@/assets/marketing-stories.jpg';
import kinkipediaImg from '@/assets/marketing-kinkipedia.jpg';
import trustImg from '@/assets/marketing-trust.jpg';
import matchingImg from '@/assets/marketing-matching.jpg';
import chatImg from '@/assets/marketing-chat.jpg';
import ctaImg from '@/assets/marketing-cta.jpg';

interface VideoFrame {
  image: string;
  title: string;
  description: string;
  duration: number; // in seconds
}

const videoFrames: VideoFrame[] = [
  {
    image: logoImg,
    title: "ALPHA",
    description: "The Future of Connection",
    duration: 3
  },
  {
    image: discoveryImg,
    title: "Smart Discovery",
    description: "Find who's looking for you with 'I'm Their Favorite'",
    duration: 4
  },
  {
    image: storiesImg,
    title: "Live Stories",
    description: "Share 15-second moments, discover nearby",
    duration: 4
  },
  {
    image: kinkipediaImg,
    title: "Kinkipedia",
    description: "Educational content library for informed connections",
    duration: 4
  },
  {
    image: trustImg,
    title: "Trust System",
    description: "Build verified, trusted connections",
    duration: 4
  },
  {
    image: matchingImg,
    title: "Smart Matching",
    description: "Advanced compatibility with private hashtags",
    duration: 4
  },
  {
    image: chatImg,
    title: "Rich Communication",
    description: "Messages, reactions, private events",
    duration: 4
  },
  {
    image: ctaImg,
    title: "Download ALPHA",
    description: "Available now - Start your journey",
    duration: 3
  }
];

const MarketingVideoViewer = () => {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const totalDuration = videoFrames.reduce((sum, frame) => sum + frame.duration, 0);

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + (100 / (videoFrames[currentFrame].duration * 10));
        
        if (newProgress >= 100) {
          if (currentFrame < videoFrames.length - 1) {
            setCurrentFrame(curr => curr + 1);
            return 0;
          } else {
            setIsPlaying(false);
            return 100;
          }
        }
        
        return newProgress;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isPlaying, currentFrame]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleRestart = () => {
    setCurrentFrame(0);
    setProgress(0);
    setIsPlaying(true);
  };

  const handleFrameClick = (index: number) => {
    setCurrentFrame(index);
    setProgress(0);
    setIsPlaying(false);
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-6 space-y-6">
      {/* Main Video Player */}
      <Card className="relative overflow-hidden bg-background/95 backdrop-blur border-border">
        <div className="relative aspect-video">
          <img 
            src={videoFrames[currentFrame].image} 
            alt={videoFrames[currentFrame].title}
            className="w-full h-full object-cover"
          />
          
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent">
            <div className="absolute bottom-6 left-6 right-6">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {videoFrames[currentFrame].title}
              </h2>
              <p className="text-muted-foreground">
                {videoFrames[currentFrame].description}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-background/20">
            <div 
              className="h-full bg-primary transition-all duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Controls */}
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePlayPause}
              className="hover-scale"
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {isPlaying ? 'Pause' : 'Play'}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRestart}
              className="hover-scale"
            >
              <RotateCcw className="h-4 w-4" />
              Restart
            </Button>
          </div>

          <div className="text-sm text-muted-foreground">
            Frame {currentFrame + 1} of {videoFrames.length} • {totalDuration}s total
          </div>
        </div>
      </Card>

      {/* Frame Timeline */}
      <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
        {videoFrames.map((frame, index) => (
          <Card 
            key={index}
            className={`cursor-pointer transition-all duration-200 hover-scale ${
              currentFrame === index ? 'ring-2 ring-primary' : 'hover:ring-1 hover:ring-border'
            }`}
            onClick={() => handleFrameClick(index)}
          >
            <div className="aspect-video overflow-hidden rounded-t-lg">
              <img 
                src={frame.image} 
                alt={frame.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-2">
              <div className="text-xs font-medium truncate">{frame.title}</div>
              <div className="text-xs text-muted-foreground">{frame.duration}s</div>
            </div>
          </Card>
        ))}
      </div>

      {/* Marketing Copy */}
      <Card className="p-6 text-center">
        <h3 className="text-xl font-bold mb-3">Experience ALPHA</h3>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          A sophisticated platform combining smart discovery, educational content, 
          trust-building features, and advanced matching algorithms. Connect with 
          intention, explore with confidence, and build meaningful relationships.
        </p>
      </Card>
    </div>
  );
};

export default MarketingVideoViewer;